"use client";

// Búsqueda de técnicos sobre el mapa, estilo Uber/Bolt.
//
// La decisión de diseño: el mapa ES la pantalla. "Cerca tuyo" contado en una
// lista es un número; visto en el mapa es una decisión. Abajo va un carrusel de
// tarjetas que se desliza con el pulgar: deslizar selecciona el pin, tocar un
// pin trae su tarjeta. Es el patrón de Uber, y evita el bottom-sheet arrastrable
// que en la web anda mal.
//
// Pantalla inmersiva: acá no se muestran las barras de navegación (están
// excluidas en NavSuperior/NavInferior) — como en Uber, el mapa ocupa todo y se
// sale con la flecha de volver.
import dynamic from "next/dynamic";
import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import Avatar from "../../components/Avatar";
import Icon from "../../components/Icon";
import { ApiError, api, hasSession } from "../../lib/api";
import type { PinTech } from "../../components/MapaTecnicos";

// Leaflet toca window al importarse: solo puede cargar en el navegador
const MapaTecnicos = dynamic(() => import("../../components/MapaTecnicos"), {
  ssr: false,
  loading: () => (
    <div className="absolute inset-0 grid place-items-center bg-[#101516]">
      <div className="text-[#808D88] text-sm animate-pulse">Cargando el mapa…</div>
    </div>
  ),
});

interface TechRow {
  id: string; displayName: string; city?: string | null;
  lat: number | null; lng: number | null;
  isAvailable: boolean; ratingAvg: number; ratingCount: number; jobsDone: number;
  distanceKm: number | null; priceFromGs: number | null;
  user: { id: string; kycLevel: number };
  services: { name: string }[];
}
interface Lugar { nombre: string; lat: number; lng: number }
interface Ruta { min: number; km: number; puntos: [number, number][] }
interface Especialidad { id: string; name: string }

const gs = (n: number) => `₲ ${n.toLocaleString("es-PY")}`;
const ASU = { lat: -25.2967, lng: -57.6359 };
const ESPERA_GEO_MS = 7000;
// Menos de ~400 m de arrastre no amerita ofrecer re-buscar
const UMBRAL_MOVIDO = 0.004;

export default function Buscar() {
  const [techs, setTechs] = useState<TechRow[] | null>(null);
  const [q, setQ] = useState("");
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [centro, setCentro] = useState(ASU);
  const [zona, setZona] = useState("Asunción");
  const [miPos, setMiPos] = useState<{ lat: number; lng: number } | null>(null);
  const [seleccionado, setSeleccionado] = useState<string | null>(null);
  const [soloDisponibles, setSoloDisponibles] = useState(false);
  // Los chips vienen del catálogo real, no de una lista inventada acá: son las
  // mismas especialidades que el técnico marca en su perfil.
  const [especialidades, setEspecialidades] = useState<Especialidad[]>([]);
  const [espElegida, setEspElegida] = useState<string | null>(null);
  const espRef = useRef<string | null>(null);
  const [encuadrar, setEncuadrar] = useState(0);
  const [movido, setMovido] = useState<{ lat: number; lng: number } | null>(null);
  // Dirección escrita a mano
  const [dir, setDir] = useState("");
  const [lugares, setLugares] = useState<Lugar[] | null>(null);
  const [geocodificando, setGeocodificando] = useState(false);
  const [volverA, setVolverA] = useState("/");
  const [puntoElegido, setPuntoElegido] = useState<{ lat: number; lng: number } | null>(null);
  const [sinGps, setSinGps] = useState(false);
  // El panel del "¿Dónde?" (GPS / marcar en el mapa / sugerencias), como Bolt
  const [dondeAbierto, setDondeAbierto] = useState(false);
  // Eligió "marcar en el mapa": se le muestra el gesto hasta que toque
  const [pedirToque, setPedirToque] = useState(false);
  // El camino en auto hasta el técnico elegido (minutos, km y la línea)
  const [ruta, setRuta] = useState<Ruta | null>(null);
  const cacheRutas = useRef(new Map<string, Ruta>());
  const resuelto = useRef(false);
  // Cuando el usuario ya tocó, escribió o buscó, el GPS deja de mandar: nada
  // más molesto que estar mirando una zona y que el mapa se te vaya solo.
  const interactuo = useRef(false);
  const carril = useRef<HTMLDivElement>(null);
  const scrollPorPin = useRef(false);

  useEffect(() => { setVolverA(hasSession() ? "/" : "/"); }, []);
  useEffect(() => {
    api<Especialidad[]>("/directory/especialidades").then(setEspecialidades).catch(() => undefined);
  }, []);

  const buscar = useCallback(
    async (query: string, c: { lat: number; lng: number }) => {
      setError("");
      setCargando(true);
      setMovido(null);
      const params = new URLSearchParams({ lat: String(c.lat), lng: String(c.lng) });
      if (query.trim()) params.set("q", query.trim());
      if (espRef.current) params.set("esp", espRef.current);
      try {
        const filas = await api<TechRow[]>(`/directory/search?${params}`);
        setTechs(filas);
        setCentro(c);
        setSeleccionado(filas[0]?.id ?? null);
        setEncuadrar((v) => v + 1);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "No pudimos conectarnos. Revisá tu conexión.");
        setTechs((prev) => prev ?? []);
      } finally {
        setCargando(false);
      }
    },
    [],
  );

  // Arranque estilo Bolt: el GPS marca solo dónde estás y se busca alrededor,
  // sin tocar nada. watchPosition (no getCurrentPosition) por dos motivos: el
  // punto azul te sigue si te movés, y si el permiso tarda en concederse la
  // primera lectura llega igual y te lleva a tu zona — siempre que no hayas
  // empezado a mirar otra cosa mientras tanto.
  useEffect(() => {
    const sinUbicacion = () => {
      if (resuelto.current) return;
      resuelto.current = true;
      setSinGps(true); // el aviso de "tocá el mapa donde estés"
      void buscar("", ASU);
    };
    if (!navigator.geolocation) { sinUbicacion(); return; }
    const corte = setTimeout(sinUbicacion, ESPERA_GEO_MS);
    const watch = navigator.geolocation.watchPosition(
      (p) => {
        clearTimeout(corte);
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        setMiPos(pos); // el punto azul siempre se actualiza
        setSinGps(false);
        // La búsqueda se recentra solo si el usuario no está mirando otra zona
        if (!resuelto.current) {
          resuelto.current = true;
          setZona("tu ubicación");
          void buscar("", pos);
        } else if (!interactuo.current) {
          // Llegó tarde (permiso lento) pero todavía no tocó nada: lo llevamos
          setZona("tu ubicación");
          void buscar("", pos);
          interactuo.current = true;
        }
      },
      () => { clearTimeout(corte); sinUbicacion(); },
      { timeout: ESPERA_GEO_MS - 1000, maximumAge: 15000 },
    );
    return () => { clearTimeout(corte); navigator.geolocation.clearWatch(watch); };
  }, [buscar]);

  // Con origen (tu GPS o el punto que marcaste) y un técnico elegido, se pide
  // el camino por calle: los minutos que muestra Bolt. Cacheado por par de
  // puntos — cambiar de tarjeta ida y vuelta no repite el pedido.
  const origen = puntoElegido ?? miPos;
  useEffect(() => {
    setRuta(null);
    if (!origen || !seleccionado) return;
    const t = (techs ?? []).find((x) => x.id === seleccionado);
    if (!t || t.lat == null || t.lng == null) return;
    const k = `${origen.lat.toFixed(4)},${origen.lng.toFixed(4)}>${t.id}`;
    const guardada = cacheRutas.current.get(k);
    if (guardada) { setRuta(guardada); return; }
    let vivo = true;
    const espera = setTimeout(() => {
      api<Ruta>(
        `/directory/ruta?deLat=${origen.lat}&deLng=${origen.lng}&aLat=${t.lat}&aLng=${t.lng}`,
      )
        .then((r) => {
          if (!vivo) return;
          cacheRutas.current.set(k, r);
          setRuta(r);
        })
        // Sin camino no pasa nada: queda la distancia en línea recta de siempre
        .catch(() => undefined);
    }, 250); // deslizar rápido el carrusel no dispara un pedido por tarjeta
    return () => { vivo = false; clearTimeout(espera); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seleccionado, origen?.lat, origen?.lng, techs]);

  // Tocar el mapa = "estoy acá": cae el pin ámbar y se busca alrededor.
  function marcarPunto(c: { lat: number; lng: number }) {
    interactuo.current = true;
    setPuntoElegido(c);
    setSinGps(false);
    setPedirToque(false);
    setDondeAbierto(false);
    setZona("el punto que marcaste");
    setDir("");
    void buscar(q, c);
  }

  async function buscarDireccion(texto: string, modo: "sugerir" | "ir") {
    const limpio = texto.trim();
    if (limpio.length < 3) return;
    setGeocodificando(true);
    setError("");
    try {
      const encontrados = await api<Lugar[]>(`/directory/geo?q=${encodeURIComponent(limpio)}`);
      if (encontrados.length === 0) {
        if (modo === "ir") setError("No encontramos ese lugar. Probá con el barrio o la ciudad.");
        setLugares([]);
      } else if (modo === "ir" && encontrados.length === 1) {
        void elegirLugar(encontrados[0]);
      } else {
        // Mientras tipea se muestran opciones, aunque haya una sola: elegir es
        // suyo — saltar al único resultado a mitad de tipeo arrebata el mapa.
        setLugares(encontrados);
      }
    } catch (e) {
      if (modo === "ir") setError(e instanceof ApiError ? e.message : "No pudimos ubicar ese lugar ahora.");
    } finally {
      setGeocodificando(false);
    }
  }

  // Sugerencias mientras escribe, como Bolt. El debounce largo no es capricho:
  // el geocodificador gratuito (Nominatim) acepta 1 pedido por segundo.
  useEffect(() => {
    const limpio = dir.trim();
    if (!dondeAbierto || limpio.length < 3) return;
    const t = setTimeout(() => void buscarDireccion(limpio, "sugerir"), 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dir, dondeAbierto]);

  function elegirLugar(l: Lugar) {
    interactuo.current = true;
    setLugares(null);
    setDir(l.nombre.split(",")[0]);
    setZona(l.nombre.split(",")[0]);
    setPuntoElegido({ lat: l.lat, lng: l.lng });
    void buscar(q, { lat: l.lat, lng: l.lng });
  }

  function usarMiUbicacion() {
    if (!navigator.geolocation) return;
    interactuo.current = true;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        setMiPos(pos);
        setSinGps(false);
        setZona("tu ubicación");
        setDir("");
        setPuntoElegido(null); // vuelve a mandar el GPS: el pin ámbar se retira
        void buscar(q, pos);
      },
      (err) =>
        setError(
          err.code === 1
            ? "El navegador tiene bloqueada tu ubicación: tocá el candado al lado de la dirección y permitila. Mientras, marcá tu ubicación tocando el mapa."
            : "No pudimos leer tu ubicación (señal o GPS apagado). Tocá el mapa donde estés o escribí tu barrio.",
        ),
      { timeout: 6000 },
    );
  }

  // Solo pines con coordenadas; el filtro de disponibles filtra mapa Y carrusel
  const visibles = (techs ?? []).filter((t) => !soloDisponibles || t.isAvailable);
  const pines: PinTech[] = visibles
    .filter((t): t is TechRow & { lat: number; lng: number } => t.lat != null && t.lng != null)
    .map((t) => ({
      id: t.id, displayName: t.displayName, lat: t.lat, lng: t.lng,
      isAvailable: t.isAvailable, userId: t.user.id, kycLevel: t.user.kycLevel,
      priceFromGs: t.priceFromGs,
    }));

  // Pin tocado → traer su tarjeta a la vista
  function seleccionarDesdePin(id: string) {
    setSeleccionado(id);
    scrollPorPin.current = true;
    const el = carril.current?.querySelector<HTMLElement>(`[data-tech="${id}"]`);
    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    setTimeout(() => (scrollPorPin.current = false), 600);
  }

  // Carrusel deslizado → seleccionar el pin de la tarjeta más centrada
  function alDeslizar() {
    if (scrollPorPin.current || !carril.current) return;
    const c = carril.current;
    const centroX = c.scrollLeft + c.clientWidth / 2;
    // for-of y no forEach: TypeScript no ve las asignaciones hechas dentro de
    // un callback y termina creyendo que `mejor` sigue siendo null.
    let mejorId: string | null = null;
    let mejorDist = Infinity;
    for (const el of Array.from(c.querySelectorAll<HTMLElement>("[data-tech]"))) {
      const d = Math.abs(el.offsetLeft + el.offsetWidth / 2 - centroX);
      if (d < mejorDist) { mejorDist = d; mejorId = el.dataset.tech ?? null; }
    }
    if (mejorId && mejorId !== seleccionado) setSeleccionado(mejorId);
  }

  const seMovio =
    movido &&
    (Math.abs(movido.lat - centro.lat) > UMBRAL_MOVIDO ||
      Math.abs(movido.lng - centro.lng) > UMBRAL_MOVIDO);

  return (
    <main className="fixed inset-0 bg-[#14191B] text-[#EDF1EF] overflow-hidden">
      <MapaTecnicos
        centro={centro}
        miPos={miPos}
        techs={pines}
        seleccionado={seleccionado}
        onSeleccionar={seleccionarDesdePin}
        onMovido={(c) => { interactuo.current = true; setMovido(c); }}
        onTocar={marcarPunto}
        puntoElegido={puntoElegido}
        ruta={ruta?.puntos ?? null}
        encuadrar={encuadrar}
      />

      {/* ── Buscador flotante ── */}
      <div className="absolute top-0 inset-x-0 z-20 p-3 sm:p-4 pointer-events-none">
        <div className="pointer-events-auto mx-auto max-w-xl rounded-2xl border border-white/12 bg-[#14191B]/95 backdrop-blur shadow-[0_6px_28px_rgba(0,0,0,0.5)] p-2.5">
          <div className="flex items-center gap-2">
            <Link href={volverA} aria-label="Volver"
              className="w-11 h-11 flex-none rounded-xl border border-white/12 grid place-items-center text-[#B5C0BB] hover:bg-white/5">
              <Icon name="chevron-left" size={20} />
            </Link>
            <div className="flex-1 min-w-0 flex flex-col gap-1.5">
              {/* Dónde, como Bolt: tocás el campo y te ofrece el GPS, marcar en
                  el mapa, o escribir con sugerencias mientras tipeás */}
              <div className="relative">
                <form
                  onSubmit={(e) => { e.preventDefault(); void buscarDireccion(dir, "ir"); }}
                  className="flex items-center gap-2 rounded-lg bg-[#101516] border border-white/12 px-3"
                >
                  <Icon name="map-pin" size={15} className="text-[#E8A23C] flex-none" />
                  <input
                    value={dir}
                    onChange={(e) => { setDir(e.target.value); setLugares(null); }}
                    onFocus={() => setDondeAbierto(true)}
                    onBlur={() => setTimeout(() => setDondeAbierto(false), 180)}
                    placeholder={`¿Dónde? (${zona})`}
                    aria-label="Barrio o dirección"
                    enterKeyHint="search"
                    className="flex-1 min-w-0 bg-transparent py-2.5 text-[14px] placeholder-[#808D88] focus:outline-none"
                  />
                  {geocodificando && <span className="text-[11px] text-[#808D88] animate-pulse flex-none">…</span>}
                </form>
                {dondeAbierto && (
                  <div className="absolute top-full inset-x-0 mt-1 z-30 rounded-xl border border-white/12 bg-[#1A2123] shadow-2xl overflow-hidden">
                    {/* Las dos acciones rápidas, siempre primero */}
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setDondeAbierto(false); usarMiUbicacion(); }}
                      className="w-full text-left px-3.5 py-3 text-[13.5px] font-semibold text-[#72A9D8] hover:bg-white/5 flex items-center gap-2.5"
                    >
                      <span className="w-2.5 h-2.5 rounded-full bg-[#72A9D8] ring-2 ring-white/70 flex-none" />
                      Usar mi ubicación (GPS)
                    </button>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => { setDondeAbierto(false); setPedirToque(true); }}
                      className="w-full text-left px-3.5 py-3 text-[13.5px] font-semibold text-[#E8A23C] hover:bg-white/5 flex items-center gap-2.5 border-t border-white/8"
                    >
                      <Icon name="map-pin" size={15} className="flex-none" />
                      Marcar en el mapa
                    </button>
                    {lugares && lugares.length > 0 && (
                      <ul className="border-t border-white/8">
                        {lugares.map((l) => (
                          <li key={`${l.lat}${l.lng}`}>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => elegirLugar(l)}
                              className="w-full text-left px-3.5 py-2.5 text-[13.5px] text-[#B5C0BB] hover:bg-white/5 flex items-center gap-2"
                            >
                              <Icon name="search" size={14} className="flex-none text-[#808D88]" />
                              <span className="truncate">{l.nombre}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {lugares && lugares.length === 0 && dir.trim().length >= 3 && !geocodificando && (
                      <p className="px-3.5 py-2.5 text-[12.5px] text-[#808D88] border-t border-white/8">
                        No encontramos ese lugar. Probá con el barrio o la ciudad.
                      </p>
                    )}
                  </div>
                )}
              </div>
              {/* Qué: filtra los pines y las tarjetas */}
              <form
                onSubmit={(e) => { e.preventDefault(); interactuo.current = true; void buscar(q, centro); }}
                className="flex items-center gap-2 rounded-lg bg-[#101516] border border-white/12 px-3"
              >
                <Icon name="search" size={15} className="text-[#808D88] flex-none" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="¿Qué se rompió? (pantalla, batería…)"
                  aria-label="Qué buscás"
                  type="search"
                  enterKeyHint="search"
                  className="flex-1 min-w-0 bg-transparent py-2.5 text-[14px] placeholder-[#808D88] focus:outline-none"
                />
                {q && (
                  <button type="button" aria-label="Limpiar"
                    onClick={() => { setQ(""); void buscar("", centro); }}
                    className="text-[#808D88] hover:text-[#EDF1EF] flex-none"><Icon name="x" size={15} /></button>
                )}
              </form>
            </div>
          </div>

          {/* Filtros que filtran el mapa al toque */}
          <div className="mt-2 flex items-center gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              onClick={() => setSoloDisponibles((v) => !v)}
              aria-pressed={soloDisponibles}
              className={`flex-none inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition ${
                soloDisponibles
                  ? "border-[#5CBE8F] bg-[#5CBE8F]/12 text-[#5CBE8F]"
                  : "border-white/12 bg-white/5 text-[#B5C0BB]"
              }`}
            >
              <span className={`w-2 h-2 rounded-full ${soloDisponibles ? "bg-[#5CBE8F]" : "bg-[#808D88]"}`} />
              Recibiendo ahora
            </button>
            {especialidades.map((e) => {
              const activa = espElegida === e.id;
              return (
                <button key={e.id}
                  aria-pressed={activa}
                  onClick={() => {
                    // Un chip a la vez, y tocarlo de nuevo lo apaga: es un
                    // filtro de mapa, no un formulario
                    const prox = activa ? null : e.id;
                    setEspElegida(prox);
                    espRef.current = prox;
                    interactuo.current = true;
                    void buscar(q, centro);
                  }}
                  className={`flex-none rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition ${
                    activa
                      ? "border-[#E8A23C] bg-[#E8A23C]/12 text-[#E8A23C]"
                      : "border-white/12 bg-white/5 text-[#B5C0BB] hover:bg-white/10"
                  }`}>
                  {e.name}
                </button>
              );
            })}
            <span className="flex-none ml-auto text-[12px] text-[#808D88] pr-1 tabular-nums">
              {cargando ? "buscando…" : `${visibles.length} técnico${visibles.length === 1 ? "" : "s"}`}
            </span>
          </div>
        </div>
      </div>

      {/* ── El gesto de tocar-para-marcar, cuando hace falta enseñarlo ── */}
      {(pedirToque || (sinGps && !cargando)) && (
        <div className="absolute left-1/2 -translate-x-1/2 top-[9.5rem] sm:top-40 z-20 pointer-events-none">
          <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-[13px] shadow-[0_4px_18px_rgba(0,0,0,0.5)] backdrop-blur ${
            pedirToque
              ? "border-[#E8A23C]/60 bg-[#E8A23C]/15 text-[#EDF1EF]"
              : "border-white/15 bg-[#14191B]/95 text-[#B5C0BB]"
          }`}>
            <Icon name="map-pin" size={14} className="text-[#E8A23C]" />
            {pedirToque ? "Tocá el mapa donde estés" : "Sin tu ubicación — tocá el mapa donde estés"}
          </div>
        </div>
      )}

      {/* ── Re-buscar donde quedó el mapa ── */}
      {seMovio && !cargando && (
        <div className="absolute left-1/2 -translate-x-1/2 top-[9.5rem] sm:top-40 z-20">
          <button
            onClick={() => movido && buscar(q, movido)}
            className="inline-flex items-center gap-2 rounded-full bg-[#E8A23C] text-[#201404] font-bold text-[13.5px] px-4 py-2.5 shadow-[0_4px_18px_rgba(0,0,0,0.5)] hover:brightness-105"
          >
            <Icon name="search" size={15} /> Buscar en esta zona
          </button>
        </div>
      )}

      {/* ── Mi ubicación ── */}
      <button
        onClick={usarMiUbicacion}
        aria-label="Usar mi ubicación"
        className="absolute right-3 bottom-[13.5rem] sm:bottom-56 z-20 w-12 h-12 rounded-full border border-white/12 bg-[#14191B]/95 backdrop-blur grid place-items-center text-[#72A9D8] shadow-[0_4px_18px_rgba(0,0,0,0.5)] hover:bg-[#1A2123]"
      >
        <Icon name="map-pin" size={20} />
      </button>

      {error && (
        <div className="absolute inset-x-3 bottom-[13.5rem] sm:bottom-56 z-20 mx-auto max-w-md rounded-xl bg-[#DE705C]/15 border border-[#DE705C]/30 backdrop-blur px-4 py-2.5 text-[13px] text-[#EDF1EF]">
          {error}
        </div>
      )}

      {/* ── Carrusel de tarjetas: deslizar = elegir pin ── */}
      <div className="absolute bottom-0 inset-x-0 z-20 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        {cargando && !techs ? (
          <div className="px-4 flex gap-3">
            {[0, 1].map((i) => (
              <div key={i} className="w-[19rem] h-40 flex-none rounded-2xl bg-[#1A2123]/95 border border-white/10 animate-pulse" />
            ))}
          </div>
        ) : visibles.length === 0 && techs ? (
          <div className="mx-4 mb-1 max-w-md sm:mx-auto rounded-2xl border border-white/12 bg-[#14191B]/95 backdrop-blur p-5 text-center">
            <p className="text-[14px] text-[#B5C0BB]">
              {soloDisponibles
                ? "Nadie está recibiendo equipos en esta zona ahora."
                : q
                  ? `No encontramos técnicos para “${q}” por acá.`
                  : "Todavía no hay técnicos publicados en esta zona."}
            </p>
            <button
              onClick={() => { setSoloDisponibles(false); setQ(""); void buscar("", centro); }}
              className="mt-3 min-h-[44px] px-5 rounded-xl border border-white/15 text-sm font-bold hover:bg-white/5"
            >
              Ver todos
            </button>
          </div>
        ) : (
          <div
            ref={carril}
            onScroll={alDeslizar}
            className="flex gap-3 overflow-x-auto px-4 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {visibles.map((t) => {
              const activo = t.id === seleccionado;
              return (
                <article
                  key={t.id}
                  data-tech={t.id}
                  onClick={() => setSeleccionado(t.id)}
                  className={`w-[19rem] flex-none snap-center rounded-2xl border backdrop-blur p-4 transition cursor-pointer ${
                    activo
                      ? "border-[#E8A23C]/60 bg-[#1A2123]/98 shadow-[0_6px_28px_rgba(0,0,0,0.55)]"
                      : "border-white/10 bg-[#1A2123]/88"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar userId={t.user.id} nombre={t.displayName} size={46} className="!rounded-full" />
                    <div className="flex-1 min-w-0">
                      <strong className="text-[15px] block truncate">{t.displayName}</strong>
                      <div className="text-[12.5px] text-[#808D88] truncate">
                        {t.ratingCount > 0
                          ? <><Icon name="star" size={12} className="inline align-[-1px] text-[#E8A23C]" /> {t.ratingAvg} ({t.ratingCount})</>
                          : "sin reseñas aún"}
                        {t.distanceKm != null && <> · a {t.distanceKm} km</>}
                      </div>
                    </div>
                    <span
                      className={`flex-none w-2.5 h-2.5 rounded-full ${t.isAvailable ? "bg-[#5CBE8F]" : "bg-[#808D88]"}`}
                      title={t.isAvailable ? "Recibiendo equipos" : "No disponible"}
                    />
                  </div>

                  <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                    {t.user.kycLevel >= 1 && (
                      <span className="text-2xs font-semibold bg-[#5CBE8F]/15 text-[#5CBE8F] rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Icon name="check" size={12} /> Verificado
                      </span>
                    )}
                    {t.user.kycLevel >= 3 && (
                      <span className="text-2xs font-semibold bg-[#E8A23C]/15 text-[#E8A23C] rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Icon name="badge-check" size={12} /> Oficio validado
                      </span>
                    )}
                    {t.services[0] && (
                      <span className="text-2xs text-[#808D88] truncate">{t.services[0].name}</span>
                    )}
                  </div>

                  {activo && ruta && (
                    <div className="mt-2.5 inline-flex items-center gap-1.5 rounded-lg bg-[#E8A23C]/12 text-[#E8A23C] px-2.5 py-1.5 text-[12.5px] font-bold">
                      <Icon name="clock" size={13} />
                      {ruta.min} min en auto · {String(ruta.km).replace(".", ",")} km por calle
                    </div>
                  )}

                  <div className="mt-3 flex items-center gap-2">
                    {t.priceFromGs != null && (
                      <span className="font-mono text-[13px] text-[#B5C0BB] whitespace-nowrap">
                        desde <strong className="text-[#EDF1EF]">{gs(t.priceFromGs)}</strong>
                      </span>
                    )}
                    <Link
                      href={`/tec/${t.id}`}
                      prefetch={false}
                      onClick={(e) => e.stopPropagation()}
                      className="ml-auto inline-flex min-h-[40px] items-center gap-1.5 rounded-xl bg-[#E8A23C] text-[#201404] font-bold text-[13.5px] px-4 hover:brightness-105"
                    >
                      Ver perfil <Icon name="arrow-right" size={15} />
                    </Link>
                  </div>
                </article>
              );
            })}
            {/* Colchón para que la última tarjeta pueda centrarse */}
            <div className="w-6 flex-none" aria-hidden />
          </div>
        )}
      </div>
    </main>
  );
}
